import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { carsApi } from '../api/cars.js';
import { PlateNumber } from '../components/ui/PlateNumber.jsx';
import { UsageDot } from '../components/ui/UsageDot.jsx';
import { DocumentsTab } from '../components/documents/DocumentsTab.jsx';
import { RentalsTab } from '../components/rentals/RentalsTab.jsx';

const TABS = [
  { id: 'documents', label: 'Documente' },
  { id: 'rental', label: 'Închiriere' },
  { id: 'history', label: 'Istoric' },
];

export function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('documents');

  async function load() {
    try {
      const { car } = await carsApi.get(id);
      setCar(car);
    } catch (ex) {
      setErr(ex.body?.error === 'not_found' ? 'Mașina nu a fost găsită.' : 'Eroare.');
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function onDelete() {
    if (!confirm('Ștergi mașina? Documentele și închirierile asociate se șterg definitiv.')) return;
    try {
      await carsApi.remove(id);
      navigate('/cars');
    } catch (ex) {
      alert('Eroare la ștergere: ' + (ex.body?.error || ex.message));
    }
  }

  if (err) return <div className="card text-center text-danger">{err}</div>;
  if (!car) return <div className="card text-center text-muted">Se încarcă…</div>;

  const photoUrl = car.poza_path ? carsApi.photoUrl(car.id) : null;

  return (
    <div className="space-y-4">
      {/* Hero cu gradient ink → teal-bright + blob-uri decorative */}
      <section className="relative overflow-hidden rounded-card bg-gradient-to-br from-ink via-teal to-teal-bright p-6 text-cream">
        <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-coral/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-16 right-0 h-52 w-52 rounded-full bg-teal-bright/30 blur-3xl" aria-hidden />

        <div className="relative flex items-start justify-between gap-4">
          <Link to="/cars" className="text-xs uppercase tracking-wider text-cream/70 hover:text-cream">
            ← înapoi la mașini
          </Link>
          <div className="flex gap-2">
            <Link to={`/cars/${car.id}/edit`} className="btn bg-white/10 text-cream hover:bg-white/20">Editează</Link>
            <button onClick={onDelete} className="btn bg-danger/90 text-white hover:bg-danger">Șterge</button>
          </div>
        </div>

        <div className="relative mt-4 flex flex-col items-start gap-3 md:flex-row md:items-center">
          {photoUrl && (
            <div className="h-24 w-24 overflow-hidden rounded-[18px] border border-white/20 bg-white/5">
              <img src={photoUrl} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <div>
            <PlateNumber value={car.numar_inmatriculare} size="xl" copyable className="mb-3" />
            <h2 className="text-4xl font-semibold tracking-tight leading-none text-cream">
              {car.marca} {car.model}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-cream/80">
              {car.an && <span className="font-mono">{car.an}</span>}
              {car.kilometraj != null && (
                <span className="font-mono">{car.kilometraj.toLocaleString('ro-RO')} km</span>
              )}
              <UsageDot kind={car.tip_utilizare} />
              {car.assigned_to && (
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs">→ {car.assigned_to.name}</span>
              )}
              {!car.assigned_to && (
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-cream/60">Neasignat</span>
              )}
            </div>
          </div>
        </div>

        {car.observatii && (
          <p className="relative mt-4 max-w-2xl border-l-2 border-cream/20 pl-3 text-sm text-cream/80">
            {car.observatii}
          </p>
        )}
      </section>

      {/* Tabs */}
      <nav className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => {
          const hide = t.id === 'rental' && car.tip_utilizare !== 'inchiriat';
          if (hide) return null;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-coral text-ink'
                  : 'border-transparent text-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <section>
        {tab === 'documents' && <DocumentsTab carId={car.id} />}
        {tab === 'rental' && car.tip_utilizare === 'inchiriat' && (
          <RentalsTab carId={car.id} carKm={car.kilometraj} car={car} />
        )}
        {tab === 'history' && (
          <div className="card text-sm text-muted">
            <div className="mb-2 text-xs uppercase tracking-wider">Activitate</div>
            <ul className="space-y-2 font-mono text-xs">
              <li>Creată: {new Date(car.created_at).toLocaleString('ro-RO')}</li>
              <li>Actualizată: {new Date(car.updated_at).toLocaleString('ro-RO')}</li>
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
